import React from 'react';
import { ProfilesController, ProfilesControllerProps } from './Profiles.controller';
import { ApolloError, gql } from '@apollo/client';
import {
  useAppInstalledLazyQuery,
  useConnectMainSocialProfileMutation,
  useExternalSecondaryProfilesLazyQuery,
  useGetSocialProfilesLazyQuery,
  useReconnectSocialProfileMutation,
  useSocialAuthTokenLazyQuery,
  useStartSocialOAuthMutation,
  useUpdateSocialProfileDirectPublishingMutation
} from '../../../models/graphql';

export type ProfilesApolloProps = Omit<
  ProfilesControllerProps,
  | 'appInstalledQuery'
  | 'availablePostAsQuery'
  | 'connectMainSocialProfile'
  | 'connectProfileData'
  | 'errorForRemapping'
  | 'getSocialProfileQuery'
  | 'getProfilesData'
  | 'getSecondaryProfiles'
  | 'loading'
  | 'reconnectSocialProfileMutation'
  | 'selectedOrganizationId'
  | 'selectedWorkspaceId'
  | 'socialAuthTokenQuery'
  | 'startSocialOAuth'
  | 'updateDirectPublishing'
  | 'upgradeInstagramSocialProfileMutation'
  | 'getMobileApps'
  | 'mobileApps'
> & {
  // apollo props
};

gql`
  query GetSocialProfiles($workspaceId: ID!) {
    workspace(workspaceId: $workspaceId) {
      id
      socialProfiles {
        ...SocialProfileFields
      }
    }
  }

  mutation ConnectMainSocialProfile($workspaceId: ID!, $socialAuthToken: String!) {
    connectMainSocialProfile(workspaceId: $workspaceId, socialAuthToken: $socialAuthToken) {
      connected
      reconnected
      socialProfile {
        ...SocialProfileFields
      }
      additionalReconnection
      error
      __typename
    }
  }

  mutation ReconnectSocialProfile($socialAuthToken: String!, $profileId: ID!) {
    reconnectSocialProfile(socialAuthToken: $socialAuthToken, profileId: $profileId) {
      connected
      reconnected
      socialProfile {
        ...SocialProfileFields
        workspace {
          id
        }
      }
      additionalReconnection
      error
      __typename
    }
  }
`;

gql`
  query AppInstalled($id: ID!) {
    socialProfile(socialProfileId: $id) {
      ... on FacebookSocialProfile {
        id
        groupAppInstalled
      }
    }
  }

  query ExternalSecondaryProfiles(
    $socialAuthToken: String!
    $type: SecondaryProfileType!
    $profileNetwork: Network!
    $workspaceId: ID!
  ) {
    externalSecondaryProfiles(
      socialAuthToken: $socialAuthToken
      type: $type
      profileNetwork: $profileNetwork
      workspaceId: $workspaceId
    ) {
      externalId
      displayName
      organizationId
      workspaceId
      photoUrl
      availablePostAsProfiles {
        externalId
        displayName
      }
    }
  }

  mutation UpdateSocialProfileDirectPublishing($socialProfileId: ID!, $enabled: Boolean!) {
    updateSocialProfileDirectPublishing(socialProfileId: $socialProfileId, enabled: $enabled) {
      id
      directPublishEnabled
    }
  }
`;

export const ProfilesApollo = (props: ProfilesApolloProps): JSX.Element => {
  const [startSocialOAuth, { error: startSocialError, loading: startSocialLoading }] = useStartSocialOAuthMutation({
    fetchPolicy: 'no-cache'
  });

  const [connectMainSocialProfile, { error: connectProfileApolloError, loading: connectProfileLoading }] =
    useConnectMainSocialProfileMutation();

  const [reconnectSocialProfileMutation, { error: reconnectProfileApolloError, loading: reconnectProfileLoading }] =
    useReconnectSocialProfileMutation();

  const [getSocialProfileQuery, { loading: getProfilesLoading, data: getProfilesData, error: getProfilesError }] =
    useGetSocialProfilesLazyQuery();

  const [appInstalledQuery] = useAppInstalledLazyQuery({
    fetchPolicy: 'no-cache'
  });

  const [getSecondaryProfiles] = useExternalSecondaryProfilesLazyQuery({ fetchPolicy: 'no-cache' });

  const [updateDirectPublishing, { loading: updateDirectPublishingLoading }] =
    useUpdateSocialProfileDirectPublishingMutation();

  const [socialAuthTokenQuery] = useSocialAuthTokenLazyQuery({
    fetchPolicy: 'no-cache'
  });

  let loading = false;
  let errorForRemapping: ApolloError | null = null;

  if (
    startSocialLoading ||
    connectProfileLoading ||
    reconnectProfileLoading ||
    getProfilesLoading ||
    updateDirectPublishingLoading
    // ||
    // upgradeInstagramLoading
  ) {
    loading = true;
    errorForRemapping = null;
  }
  if (
    startSocialError ||
    connectProfileApolloError ||
    reconnectProfileApolloError ||
    getProfilesError
    // ||
    // upgradeInstagramProfileError
  ) {
    errorForRemapping = (startSocialError ||
      connectProfileApolloError ||
      reconnectProfileApolloError ||
      // upgradeInstagramProfileError ||
      getProfilesError) as ApolloError;
    loading = false;
  }

  const args: ProfilesControllerProps = {
    ...props,
    appInstalledQuery,
    connectMainSocialProfile,
    errorForRemapping,
    getProfilesData,
    getSecondaryProfiles,
    getSocialProfileQuery,
    loading,
    reconnectSocialProfileMutation,
    socialAuthTokenQuery,
    startSocialOAuth,
    updateDirectPublishing
  };

  return <ProfilesController {...args} />;
};
